import { createAuthRequestInit } from '../utils/auth';
import { API_BASE } from '../config/api';

export interface SendQuickSmsResponse {
  messageId: string;
  total: number;
  queued: number;
  invalid: string[];
  status: string;
  message: string;
}

export interface SendCampaignSmsResponse {
  messageId: string;
  campaignName: string | null;
  scheduledDate: string | null;
  scheduledTime: string | null;
  isScheduled: boolean;
  total: number;
  queued: number;
  invalid: string[];
  status: string;
  message: string;
}

export async function sendQuickSms(payload: {
  senderId?: string;
  body: string;
  recipients: string[];
}): Promise<SendQuickSmsResponse> {
  const res = await fetch(
    `${API_BASE}/sms/send`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to send SMS');
  }
  const json = await res.json();
  return json.data as SendQuickSmsResponse;
}

export async function sendCampaignSms(payload: {
  senderId?: string;
  body: string;
  recipients: string[];
  recipientMessages?: Array<{
    phone: string;
    body: string;
    fields?: Record<string, string>;
  }>;
  campaignName?: string;
  scheduledDate?: string;
  scheduledTime?: string;
}): Promise<SendCampaignSmsResponse> {
  const res = await fetch(
    `${API_BASE}/sms/campaign`,
    createAuthRequestInit(
      {
        method: 'POST',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to send campaign SMS');
  }
  const json = await res.json();
  return json.data as SendCampaignSmsResponse;
}

export interface SmsActivityPoint {
  period: string;
  total: number;
  delivered: number;
  failed: number;
  sent: number;
  queued: number;
}

export async function getSmsActivity(params?: {
  startDate?: string;
  endDate?: string;
  groupBy?: 'hour' | 'day' | 'week' | 'month';
}): Promise<SmsActivityPoint[]> {
  const queryParams = new URLSearchParams();
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);
  if (params?.groupBy) queryParams.append('groupBy', params.groupBy);

  const url = `${API_BASE}/reports/activity${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;
  const res = await fetch(url, createAuthRequestInit());
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to fetch SMS activity');
  }
  const json = await res.json();
  const data = Array.isArray(json.data) ? json.data : [];
  return data.map((item: any) => ({
    period: String(item?.period ?? ''),
    total: Number(item?.total ?? 0),
    delivered: Number(item?.delivered ?? 0),
    failed: Number(item?.failed ?? 0),
    sent: Number(item?.sent ?? 0),
    queued: Number(item?.queued ?? 0)
  }));
}

export interface ScheduledCampaign {
  id: string;
  campaignName: string | null;
  scheduledDate: string | null;
  scheduledTime: string | null;
  body: string;
  senderId: string | null;
  recipientCount: number;
  createdAt: Date | string;
}

export async function getScheduledCampaigns(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<ScheduledCampaign[]> {
  const queryParams = new URLSearchParams();
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);

  const url = `${API_BASE}/sms/scheduled${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const res = await fetch(url, createAuthRequestInit());
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to fetch scheduled campaigns');
  }
  const json = await res.json();
  return json.data as ScheduledCampaign[];
}

export async function getCampaignRecipients(campaignId: string): Promise<string[]> {
  const res = await fetch(
    `${API_BASE}/sms/campaign/${campaignId}/recipients`,
    createAuthRequestInit()
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to fetch campaign recipients');
  }
  const json = await res.json();
  return json.data as string[];
}

export async function updateCampaign(campaignId: string, payload: {
  campaignName?: string;
  scheduledDate?: string;
  scheduledTime?: string;
  body?: string;
  senderId?: string;
  recipients?: string[];
}): Promise<ScheduledCampaign> {
  const res = await fetch(
    `${API_BASE}/sms/campaign/${campaignId}`,
    createAuthRequestInit(
      {
        method: 'PUT',
        body: JSON.stringify(payload)
      },
      true
    )
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to update campaign');
  }
  const json = await res.json();
  return json.data as ScheduledCampaign;
}

export interface DashboardStats {
  inProgress: number;
  sent: number;
  failed: number;
  scheduled: number;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  const res = await fetch(`${API_BASE}/reports/dashboard-stats`, createAuthRequestInit());
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Failed to fetch dashboard stats');
  }
  const json = await res.json();
  return json.data as DashboardStats;
}


